import {
  Breakpoint,
  Dialog,
  DialogProps,
  Paper,
  PaperProps,
  useMediaQuery,
  styled,
  Theme,
} from '@nexelem/deprecated-theme';
import { ComponentProps, JSX, JSXElementConstructor, ReactNode } from 'react';
import { ModalContent } from './ModalContent';
import { ModalContext } from './ModalContext';
import { ModalFooter } from './ModalFooter';
import { ModalHeader } from './ModalHeader';
import { ModalFullScreenFooter } from './ModalFullScreenFooter';

type NewModalProps<T extends keyof JSX.IntrinsicElements | JSXElementConstructor<any> = 'div'> = Pick<
  DialogProps,
  'fullWidth' | 'fullScreen'
> &
  Pick<StyledPaperProps, 'maxWidth'> &
  Partial<Omit<ComponentProps<T>, 'ref' | 'children'>> & {
    readonly children: ReactNode;
    readonly onClose: () => void;
    readonly isOpen?: boolean;
    readonly component?: T;
    readonly disableBackdropClick?: boolean;
  };

type StyledPaperProps = PaperProps & {
  maxWidth?: Breakpoint;
  isFullScreen: boolean;
};

const breakpoints: Record<Breakpoint, number> = {
  xs: 272,
  sm: 398,
  md: 448,
  lg: 608,
  xl: 928,
};

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => !['isFullScreen', 'maxWidth'].includes(prop as string),
})<StyledPaperProps>(({ theme, maxWidth, isFullScreen }) => {
  const {
    palette: {
      getColor,
      elevation: { medium },
    },
    client: {
      modals: {
        default: { background },
      },
    },
  } = theme as Theme;

  return {
    '&&': {
      ...medium,
      maxWidth: isFullScreen ? '100vw' : `${breakpoints[(maxWidth as Breakpoint) || 'md']}px`,
      backgroundColor: getColor(background),
    },
  };
});

export const StaticModal = <T extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>>({
  isOpen = true,
  onClose,
  children,
  maxWidth = 'md',
  fullScreen,
  component,
  disableBackdropClick = true,
  ...props
}: NewModalProps<T>) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down(480));
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down(768));
  const isFullScreen = fullScreen || isMobile || (isTablet && !['sm', 'sx'].includes(maxWidth));

  const handleClose = (_: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  return (
    <ModalContext.Provider value={{ onClose }}>
      <Dialog
        {...props}
        fullWidth
        component={component}
        fullScreen={isFullScreen}
        open={isOpen}
        PaperComponent={StyledPaper as any}
        scroll="paper"
        PaperProps={{
          maxWidth,
          isFullScreen,
        }}
        onClose={handleClose}
      >
        {children}
      </Dialog>
    </ModalContext.Provider>
  );
};

StaticModal.displayName = 'Modal';

// share with the main component to less imports
StaticModal.Header = ModalHeader;
StaticModal.Content = ModalContent;
StaticModal.Footer = ModalFooter;
StaticModal.FullscreenFooter = ModalFullScreenFooter;
